import type { CSSProperties, ReactNode } from "react";
import { Marked, type Token, type Tokens } from "marked";
import { truncateMarkdownSource } from "@cabal/shared/util/markdown-cut.js";
import { Cmarkdowncode } from "../Cmarkdowncode/Cmarkdowncode";
import { Cmarkdownmath } from "../Cmarkdownmath/Cmarkdownmath";
import { Cmarkdownlink } from "../Cmarkdownlink/Cmarkdownlink";
import { Cmarkdowncolor } from "../Cmarkdowncolor/Cmarkdowncolor";
import { Cmarkdownimage } from "../Cmarkdownimage/Cmarkdownimage";
import { Cmarkdowndate } from "../Cmarkdowndate/Cmarkdowndate";
import { Creference } from "../Creference/Creference";
// The Cmarkdown↔Cresultcard edge is a module cycle: the kit's cards render
// markdown, so a module-scope read from this barrel can observe an
// unevaluated binding. Import at top level, dereference ONLY inside render
// logic — never at module scope.
import { crcAddressHost } from "../Cresultcard";
import { FontWeight } from "@/theme/typography";
import { createHexColorTokenizerExtension } from "./color-tokenizer";
import { HEX_COLOR_TOKEN_NAME, isHexColorToken } from "./color-types";
import { createIsoDateTokenizerExtension } from "./date-tokenizer";
import { ISO_DATE_TOKEN_NAME, isIsoDateToken } from "./date-types";
import {
  createMathBlockTokenizerExtension,
  createMathInlineTokenizerExtension,
} from "./math-tokenizer";
import { MATH_INLINE_TOKEN_NAME, isMathInlineToken } from "./math-types";
import { createParenListTokenizerExtension } from "./paren-list-tokenizer";
import { createCreferenceTokenizerExtension } from "./reference-tokenizer";
import { CREFERENCE_TOKEN_NAME, isCreferenceToken } from "./reference-types";
import { INLINE_INHERIT_TYPOGRAPHY_STYLE } from "./inline-typography-style";
import { InheritedSpanTertiaryText } from "./InheritedSpanTertiaryText";
// `MarkdownTokenType` lives in its own import-free module and is NOT
// re-exported from here: this module sits on the markdown render cycle, so a
// binding read through it can be undefined at module scope. Import the enum
// from `./markdown-token-type` directly.
import { MarkdownTokenType } from "./markdown-token-type";
import {
  MarkdownTokenEvidence,
  MarkdownTokenEvidenceLevel,
  MarkdownTokenEvidenceState,
} from "./MarkdownTokenEvidence";
const UNDERLINE_OPEN_TAG_REGEX = /^<u(\s[^>]*)?>$/i;
const UNDERLINE_CLOSE_TAG_REGEX = /^<\/u\s*>$/i;
// A link chip's label is a word, not a passage: past this budget the raw
// inline source is cut markdown-safely (house `..` ellipsis, open markers
// re-closed) and re-lexed. The sibling reference pill caps at 24; links carry
// fuller human titles, so they get the fuller budget.
const MARKDOWN_LINK_LABEL_CHAR_LIMIT = 40;
const ISO_DATE_QUICK_CHECK_REGEX = /\d{4}-\d{2}-\d{2}T/;
const INLINE_FORMATTING_TOKEN_TYPES = [
  MarkdownTokenType.Text,
  MarkdownTokenType.Escape,
  MarkdownTokenType.HTML,
  MarkdownTokenType.LineBreak,
] as const;
type InlineFormattingTokenType = (typeof INLINE_FORMATTING_TOKEN_TYPES)[number];
interface RenderInlineTokensInput {
  tokens: Token[];
  markdownParser: Marked;
  evidenceEnabled: boolean;
  underlineDepth?: number;
  /** An anchor label may contain formatting, but it may never mint another anchor. */
  insideLink?: boolean;
}
interface RenderInlineContentInput {
  text: string;
  markdownParser: Marked;
  evidenceEnabled: boolean;
}
interface ExtractInlineTokensInput {
  text: string;
  markdownParser: Marked;
}
interface InlineRenderState {
  nodes: ReactNode[];
  underlineDepth: number;
}
interface InlineTokenRendererInput {
  token: Token;
  key: string;
  markdownParser: Marked;
  underlineDepth: number;
  insideLink: boolean;
  evidenceEnabled: boolean;
  underlineStyle?: CSSProperties;
}
type InlineTokenRenderer = (
  input: InlineTokenRendererInput,
) => ReactNode | null;
export function createMarkdownParser(): Marked {
  const parser = new Marked({
    breaks: true,
    gfm: true,
  });
  parser.use({
    extensions: [
      createMathBlockTokenizerExtension(),
      createMathInlineTokenizerExtension(),
      createParenListTokenizerExtension(),
      createCreferenceTokenizerExtension(),
      createIsoDateTokenizerExtension(),
      createHexColorTokenizerExtension(),
    ],
  });
  return parser;
}
/**
 * One parser for the whole app.
 *
 * A `Marked` instance is only a config holder: `lexer()` delegates to
 * `Lexer.lex()`, which builds a FRESH `Lexer` per call, so every scrap of
 * lexing state (token list, inline queue, link defs, `state.top`) lives and
 * dies inside one synchronous call. The instance itself is mutated only by
 * `use()`/`setOptions()`, and this module calls `use()` exactly once, at
 * construction, with pure tokenizer extensions (no closures over mutable
 * state, no `/g` regexes, no walkTokens, no hooks, no async). Building one per
 * component was pure waste — a profiled interaction paid for 106 of them.
 */
export const SHARED_MARKDOWN_PARSER: Marked = createMarkdownParser();
function extractInlineTokensFromText(
  input: ExtractInlineTokensInput,
): Token[] | null {
  if (!shouldParseInlineText(input.text)) {
    return null;
  }
  try {
    const tokens = input.markdownParser.lexer(input.text);
    const inlineTokens = collectParagraphInlineTokens(tokens);
    if (!hasInlineFormatting(inlineTokens)) {
      return null;
    }
    return inlineTokens;
  } catch {
    return null;
  }
}
function shouldParseInlineText(text: string): boolean {
  return (
    text.includes("*") ||
    text.includes("$") ||
    text.includes("#") ||
    ISO_DATE_QUICK_CHECK_REGEX.test(text)
  );
}
function collectParagraphInlineTokens(tokens: Token[]): Token[] {
  return tokens.reduce<Token[]>((accumulator, token) => {
    if (isParagraphTokenWithChildren(token)) {
      return [...accumulator, ...token.tokens];
    }
    return accumulator;
  }, []);
}
function isParagraphTokenWithChildren(token: Token): token is Tokens.Paragraph {
  return (
    token.type === MarkdownTokenType.Paragraph &&
    "tokens" in token &&
    Boolean(token.tokens)
  );
}
function hasInlineFormatting(tokens: Token[]): boolean {
  return tokens.some(hasNonTextInlineFormatting);
}
function hasNonTextInlineFormatting(token: Token): boolean {
  return !INLINE_FORMATTING_TOKEN_TYPES.includes(
    token.type as InlineFormattingTokenType,
  );
}
function isEscapedMarkdownCharacter(chars: string[], index: number): boolean {
  let backslashCount = 0;
  for (
    let charIndex = index - 1;
    charIndex >= 0 && chars[charIndex] === "\\";
    charIndex -= 1
  ) {
    backslashCount += 1;
  }
  return backslashCount % 2 === 1;
}
function stripOrphanSingleAsterisks(text: string): string {
  if (!text.includes("*")) {
    return text;
  }
  const chars = [...text];
  const singleStarIndices = chars.reduce<number[]>(collectSingleStarIndex, []);
  if (singleStarIndices.length % 2 === 0) {
    return text;
  }
  const orphanIndex = singleStarIndices[singleStarIndices.length - 1];
  return chars.filter((_, index) => index !== orphanIndex).join("");
}
function collectSingleStarIndex(
  indices: number[],
  char: string,
  index: number,
  chars: string[],
): number[] {
  if (!isSingleStar({ char, index, chars })) {
    return indices;
  }
  return [...indices, index];
}
function isSingleStar(input: {
  char: string;
  index: number;
  chars: string[];
}): boolean {
  return (
    input.char === "*" &&
    !isEscapedMarkdownCharacter(input.chars, input.index) &&
    !isPartOfDoubleAsterisk({ chars: input.chars, index: input.index })
  );
}
function isPartOfDoubleAsterisk(input: {
  chars: string[];
  index: number;
}): boolean {
  const prevChar = input.index > 0 ? input.chars[input.index - 1] : "";
  const nextChar =
    input.index < input.chars.length - 1 ? input.chars[input.index + 1] : "";
  return prevChar === "*" || nextChar === "*";
}
export function renderInlineContent(
  input: RenderInlineContentInput,
): ReactNode {
  if (!input.text) {
    return null;
  }
  try {
    const tokens = input.markdownParser.lexer(input.text);
    const inlineTokens = tokens.reduce<Token[]>((accumulator, token) => {
      if (
        token.type === MarkdownTokenType.Paragraph &&
        "tokens" in token &&
        token.tokens
      ) {
        return [...accumulator, ...token.tokens];
      }
      return accumulator;
    }, []);
    if (inlineTokens.length === 0) {
      return stripOrphanSingleAsterisks(input.text);
    }
    return renderInlineTokens({
      tokens: inlineTokens,
      markdownParser: input.markdownParser,
      evidenceEnabled: input.evidenceEnabled,
    });
  } catch {
    return stripOrphanSingleAsterisks(input.text);
  }
}
export function renderInlineTokens(input: RenderInlineTokensInput): ReactNode {
  const initialState: InlineRenderState = {
    nodes: [],
    underlineDepth: input.underlineDepth ?? 0,
  };
  const renderedState = input.tokens.reduce<InlineRenderState>(
    (state, token, index) => {
      return renderInlineTokenIntoState({
        state,
        token,
        key: `token-${index}`,
        markdownParser: input.markdownParser,
        insideLink: input.insideLink ?? false,
        evidenceEnabled: input.evidenceEnabled,
      });
    },
    initialState,
  );
  return renderedState.nodes;
}
function renderInlineTokenIntoState(input: {
  state: InlineRenderState;
  token: Token;
  key: string;
  markdownParser: Marked;
  insideLink: boolean;
  evidenceEnabled: boolean;
}): InlineRenderState {
  const nextUnderlineDepth = resolveUnderlineDepth({
    token: input.token,
    currentDepth: input.state.underlineDepth,
  });
  if (nextUnderlineDepth != null) {
    return {
      ...input.state,
      nodes: [
        ...input.state.nodes,
        <MarkdownTokenEvidence
          enabled={input.evidenceEnabled}
          key={`${input.key}-evidence`}
          level={MarkdownTokenEvidenceLevel.Inline}
          source={inlineTokenSource(input.token)}
          state={MarkdownTokenEvidenceState.Control}
          tokenType={input.token.type}
        >
          {null}
        </MarkdownTokenEvidence>,
      ],
      underlineDepth: nextUnderlineDepth,
    };
  }
  const underlineStyle = getUnderlineStyle(input.state.underlineDepth);
  const renderedNode = renderInlineToken({
    token: input.token,
    key: input.key,
    markdownParser: input.markdownParser,
    underlineDepth: input.state.underlineDepth,
    insideLink: input.insideLink,
    evidenceEnabled: input.evidenceEnabled,
    underlineStyle,
  });
  return {
    ...input.state,
    nodes: [
      ...input.state.nodes,
      <MarkdownTokenEvidence
        enabled={input.evidenceEnabled}
        key={`${input.key}-evidence`}
        level={MarkdownTokenEvidenceLevel.Inline}
        source={inlineTokenSource(input.token)}
        state={
          renderedNode == null
            ? MarkdownTokenEvidenceState.Dropped
            : MarkdownTokenEvidenceState.Rendered
        }
        tokenType={input.token.type}
      >
        {renderedNode}
      </MarkdownTokenEvidence>,
    ],
  };
}
function inlineTokenSource(token: Token): string {
  if ("raw" in token && typeof token.raw === "string") return token.raw;
  if ("text" in token && typeof token.text === "string") return token.text;
  return "";
}
function resolveUnderlineDepth(input: {
  token: Token;
  currentDepth: number;
}): number | null {
  if (input.token.type !== MarkdownTokenType.HTML) {
    return null;
  }
  const htmlToken = input.token as Tokens.HTML;
  const htmlText = htmlToken.text.trim();
  if (UNDERLINE_OPEN_TAG_REGEX.test(htmlText)) {
    return input.currentDepth + 1;
  }
  if (UNDERLINE_CLOSE_TAG_REGEX.test(htmlText)) {
    return Math.max(0, input.currentDepth - 1);
  }
  return null;
}
function getUnderlineStyle(underlineDepth: number): CSSProperties | undefined {
  return underlineDepth > 0 ? { textDecoration: "underline" } : undefined;
}
const INLINE_TOKEN_RENDERERS: Record<string, InlineTokenRenderer> = {
  [MarkdownTokenType.Strong]: renderStrongToken,
  [MarkdownTokenType.Emphasis]: renderEmphasisToken,
  [MarkdownTokenType.CodeSpan]: renderCodeSpanToken,
  [HEX_COLOR_TOKEN_NAME]: renderHexColorToken,
  [MATH_INLINE_TOKEN_NAME]: renderMathInlineToken,
  [MarkdownTokenType.Link]: renderLinkToken,
  [MarkdownTokenType.Image]: renderImageToken,
  [CREFERENCE_TOKEN_NAME]: renderCreferenceToken,
  [ISO_DATE_TOKEN_NAME]: renderIsoDateToken,
  [MarkdownTokenType.Text]: renderTextToken,
  [MarkdownTokenType.Escape]: renderEscapeToken,
  [MarkdownTokenType.LineBreak]: renderLineBreakToken,
  [MarkdownTokenType.Del]: renderDeletedToken,
};
function renderInlineToken(input: InlineTokenRendererInput): ReactNode | null {
  const renderer = INLINE_TOKEN_RENDERERS[input.token.type];
  if (renderer) {
    return renderer(input);
  }
  return renderFallbackInlineToken(input);
}
function renderStrongToken(input: InlineTokenRendererInput): ReactNode {
  const strongToken = input.token as Tokens.Strong;
  return (
    <strong
      key={input.key}
      style={{
        fontWeight: FontWeight.Heavy,
        color: "rgb(102, 254, 193)",
        opacity: 0.85,
        paddingRight: "3px",
        ...input.underlineStyle,
        ...INLINE_INHERIT_TYPOGRAPHY_STYLE,
      }}
    >
      {strongToken.tokens
        ? renderInlineTokens({
            tokens: strongToken.tokens,
            markdownParser: input.markdownParser,
            underlineDepth: input.underlineDepth,
            insideLink: input.insideLink,
            evidenceEnabled: input.evidenceEnabled,
          })
        : strongToken.text}
    </strong>
  );
}
function renderEmphasisToken(input: InlineTokenRendererInput): ReactNode {
  const emToken = input.token as Tokens.Em;
  return (
    <em
      key={input.key}
      style={{
        fontStyle: "italic",
        ...input.underlineStyle,
        ...INLINE_INHERIT_TYPOGRAPHY_STYLE,
      }}
    >
      {emToken.tokens
        ? renderInlineTokens({
            tokens: emToken.tokens,
            markdownParser: input.markdownParser,
            underlineDepth: input.underlineDepth,
            insideLink: input.insideLink,
            evidenceEnabled: input.evidenceEnabled,
          })
        : emToken.text}
    </em>
  );
}
function renderCodeSpanToken(input: InlineTokenRendererInput): ReactNode {
  const codeToken = input.token as Tokens.Codespan;
  return (
    <Cmarkdowncode key={input.key} inline={true} style={input.underlineStyle}>
      {codeToken.text}
    </Cmarkdowncode>
  );
}
function renderHexColorToken(
  input: InlineTokenRendererInput,
): ReactNode | null {
  if (!isHexColorToken(input.token)) {
    return null;
  }
  return (
    <Cmarkdowncolor
      key={input.key}
      hex={input.token.hex}
      style={input.underlineStyle}
    />
  );
}
function renderMathInlineToken(
  input: InlineTokenRendererInput,
): ReactNode | null {
  if (!isMathInlineToken(input.token)) {
    return null;
  }
  return (
    <Cmarkdownmath
      key={input.key}
      latex={input.token.text}
      displayMode={input.token.displayMode}
      style={input.underlineStyle}
    />
  );
}
function renderLinkToken(input: InlineTokenRendererInput): ReactNode {
  const linkToken = input.token as Tokens.Link;
  const label = linkToken.tokens
    ? renderInlineTokens({
        tokens: linkToken.tokens,
        markdownParser: input.markdownParser,
        underlineDepth: input.underlineDepth,
        insideLink: true,
        evidenceEnabled: input.evidenceEnabled,
      })
    : linkToken.text;
  if (input.insideLink) {
    return renderInheritedSpan({
      key: input.key,
      style: input.underlineStyle,
      children: label,
    });
  }
  return (
    <Cmarkdownlink
      key={input.key}
      href={linkToken.href}
      title={linkToken.title || undefined}
      style={input.underlineStyle}
    >
      {label}
    </Cmarkdownlink>
  );
}
function renderImageToken(input: InlineTokenRendererInput): ReactNode {
  const imageToken = input.token as Tokens.Image;
  return (
    <Cmarkdownimage
      key={input.key}
      src={imageToken.href}
      alt={imageToken.text}
      title={imageToken.title || undefined}
    />
  );
}
function renderCreferenceToken(
  input: InlineTokenRendererInput,
): ReactNode | null {
  if (!isCreferenceToken(input.token)) {
    return null;
  }
  return (
    <Creference
      key={input.key}
      entityId={input.token.shortId}
      style={input.underlineStyle}
    />
  );
}
function renderIsoDateToken(input: InlineTokenRendererInput): ReactNode | null {
  if (!isIsoDateToken(input.token)) {
    return null;
  }
  return (
    <Cmarkdowndate
      key={input.key}
      isoString={input.token.isoString}
      style={input.underlineStyle}
    />
  );
}
function renderTextToken(input: InlineTokenRendererInput): ReactNode {
  const textToken = input.token as Tokens.Text;
  // Marked already parsed link labels. Re-lexing a bare linked URL whose
  // fragment contains `#` creates the same link inside itself forever, which
  // React eventually rejects as nested anchors and drops the containing
  // paragraph. Keep the label text literal while preserving any formatting
  // tokens Marked supplied alongside it.
  const inlineTokens = input.insideLink
    ? null
    : extractInlineTokensFromText({
        text: textToken.text,
        markdownParser: input.markdownParser,
      });
  if (inlineTokens) {
    return (
      <span key={input.key} aria-hidden="true" style={input.underlineStyle}>
        {renderInlineTokens({
          tokens: inlineTokens,
          markdownParser: input.markdownParser,
          underlineDepth: input.underlineDepth,
          insideLink: input.insideLink,
          evidenceEnabled: input.evidenceEnabled,
        })}
      </span>
    );
  }
  return renderInheritedSpan({
    key: input.key,
    style: input.underlineStyle,
    children: stripOrphanSingleAsterisks(textToken.text),
  });
}
function renderEscapeToken(input: InlineTokenRendererInput): ReactNode {
  const escapeToken = input.token as Tokens.Escape;
  return renderInheritedSpan({
    key: input.key,
    style: input.underlineStyle,
    children: escapeToken.text,
  });
}
function renderLineBreakToken(input: InlineTokenRendererInput): ReactNode {
  return <br key={input.key} aria-hidden="true" />;
}
function renderDeletedToken(input: InlineTokenRendererInput): ReactNode {
  const delToken = input.token as Tokens.Del;
  return (
    <s
      key={input.key}
      style={{
        textDecoration: "line-through",
        ...input.underlineStyle,
        ...INLINE_INHERIT_TYPOGRAPHY_STYLE,
      }}
    >
      {delToken.tokens
        ? renderInlineTokens({
            tokens: delToken.tokens,
            markdownParser: input.markdownParser,
            underlineDepth: input.underlineDepth,
            insideLink: input.insideLink,
            evidenceEnabled: input.evidenceEnabled,
          })
        : delToken.text}
    </s>
  );
}
function renderFallbackInlineToken(
  input: InlineTokenRendererInput,
): ReactNode | null {
  const childTokens = (
    input.token as {
      tokens?: Token[];
    }
  ).tokens;
  if (childTokens && childTokens.length > 0) {
    return renderInheritedSpan({
      key: input.key,
      style: input.underlineStyle,
      children: renderInlineTokens({
        tokens: childTokens,
        markdownParser: input.markdownParser,
        underlineDepth: input.underlineDepth,
        insideLink: input.insideLink,
        evidenceEnabled: input.evidenceEnabled,
      }),
    });
  }
  if ("text" in input.token) {
    return renderInheritedSpan({
      key: input.key,
      style: input.underlineStyle,
      children: stripOrphanSingleAsterisks(
        (
          input.token as {
            text: string;
          }
        ).text,
      ),
    });
  }
  return null;
}
function renderInheritedSpan(input: {
  key: string;
  style?: CSSProperties;
  children: ReactNode;
}): ReactNode {
  // Key must sit on this component instance — it is an item in
  // renderInlineTokens()'s returned array (e.g. table cell content).
  // A key on an inner DOM node inside InheritedSpanTertiaryText does not
  // satisfy React's list-key requirement for that array.
  return (
    <InheritedSpanTertiaryText key={input.key} style={input.style}>
      {input.children}
    </InheritedSpanTertiaryText>
  );
}
